import React, { useEffect, useState } from "react";
import { Loader2, Repeat, XCircle, ExternalLink, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  active:       { color: "text-emerald-300", icon: CheckCircle2 },
  authenticated:{ color: "text-emerald-300", icon: CheckCircle2 },
  created:      { color: "text-slate-300",   icon: Clock },
  pending:      { color: "text-amber-300",   icon: Clock },
  halted:       { color: "text-rose-300",    icon: AlertTriangle },
  cancelled:    { color: "text-slate-400",   icon: XCircle },
  expired:      { color: "text-slate-500",   icon: XCircle },
  completed:    { color: "text-cyan-300",    icon: CheckCircle2 },
};

export default function Subscriptions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/subscriptions/me"); setItems(data); }
    catch { toast.error("Failed to load subscriptions"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cancel = async (s) => {
    if (!window.confirm(`Cancel subscription ${s.product_name}?`)) return;
    try {
      await api.post(`/subscriptions/cancel/${s.razorpay_subscription_id}`);
      toast.success("Cancellation submitted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-white" style={{fontFamily:'Outfit'}}>My Subscriptions</h2>
        <p className="text-slate-400 text-sm">Active recurring plans powered by Razorpay Subscriptions (UPI AutoPay, card mandate).</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-blue-400"/></div>
      ) : items.length === 0 ? (
        <div className="card p-8 text-center" data-testid="subs-empty">
          <Repeat size={32} className="mx-auto text-slate-500 mb-3"/>
          <p className="text-slate-300">No subscriptions yet.</p>
          <p className="text-slate-500 text-sm mt-1">Subscribe to a monthly plan on the <a href="/pricing" className="text-cyan-400">pricing page</a> to get started.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="subs-grid">
          {items.map((s) => {
            const meta = STATUS_STYLE[s.status] || STATUS_STYLE.pending;
            const Icon = meta.icon;
            return (
              <div key={s.id} className="card p-5" data-testid={`sub-${s.razorpay_subscription_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-semibold">{s.product_name}</div>
                    <div className="text-xs text-slate-500 capitalize">{s.category} · monthly</div>
                  </div>
                  <span className={`inline-flex items-center gap-1 text-xs ${meta.color}`}>
                    <Icon size={14}/> {s.status}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-slate-500 text-xs">Amount</div><div className="text-white">₹{s.amount_inr.toLocaleString()}/mo</div></div>
                  <div><div className="text-slate-500 text-xs">Cycles paid</div><div className="text-white">{s.paid_count || 0} / {s.total_count}</div></div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  {s.short_url && (s.status === "created" || s.status === "pending") && (
                    <a href={s.short_url} target="_blank" rel="noreferrer" className="btn-primary !py-2 !px-3 text-xs inline-flex items-center gap-1" data-testid={`authorize-${s.razorpay_subscription_id}`}>
                      <ExternalLink size={12}/> Authorize mandate
                    </a>
                  )}
                  {!["cancelled","expired","completed"].includes(s.status) && (
                    <button onClick={()=>cancel(s)} className="btn-ghost !py-2 !px-3 text-xs ml-auto" data-testid={`cancel-${s.razorpay_subscription_id}`}>
                      <XCircle size={12}/> Cancel
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
